import React from 'react';
import Row from '@amzn/meridian/row';
import MaestroCard from './MaestroCard';
import { CompareFlowMonitoringDataOutput, ShiftKpiComparison, ShiftTimeContext } from '../model';
import { ShiftKpiType } from '../constants';
import { VolumeProjectionSummary, deriveKpiProjection } from '../utils';

interface MaestroCardsRowProps {
    /** Volume (ALPS) projection — same object the pace banner renders. */
    projection: VolumeProjectionSummary;
    /** Server-computed KPI cards (TPH / Support Rate / HC); Volume stays on ALPS. */
    shiftKpiComparisons?: CompareFlowMonitoringDataOutput['shiftKpiComparisons'];
    /** Shift window, for the client-side Volume EOS projection. */
    shiftTimeContext?: ShiftTimeContext;
    /** Snapshot instant used for projection (shared with the banner). */
    nowMs: number;
}

/**
 * The four Maestro summary cards (Volume / TPH / Support Rate / HC). Volume comes
 * from the ALPS volume-target comparison; the other three from the server-computed
 * `shiftKpiComparisons` (by `kpiType`), which the backend populates. Each card's
 * PROJECTED EOS is derived client-side via `deriveKpiProjection` (Volume
 * extrapolates by shift pace; TPH holds current). Support Rate has no projection and
 * HC's hours-delta-to-goal is not computed by the backend, so both cards render no
 * bottom section rather than an empty "—" placeholder. Volume renders whole numbers.
 */
export const CARD_CONFIGS = [
    { key: 'volume', kpiType: ShiftKpiType.VOLUME_COMPLETED, heading: 'VOLUME — DONE SO FAR', toLabel: 'to plan', planLabel: 'ALPS plan', projectedToLabel: 'to ALPS', bottomVariant: 'projectedEos', wholeNumber: true },
    { key: 'tph', kpiType: ShiftKpiType.TPH, heading: 'TPH — CUMULATIVE', toLabel: 'to OP', planLabel: 'OP plan', projectedToLabel: 'to OP', bottomVariant: 'projectedEos', wholeNumber: false },
    { key: 'support', kpiType: ShiftKpiType.SUPPORT_RATE, heading: 'SUPPORT RATE — CUMULATIVE', toLabel: 'to target', planLabel: 'plan', projectedToLabel: 'to target', bottomVariant: 'none', wholeNumber: false },
    { key: 'hc', kpiType: ShiftKpiType.HC_STAFFED, heading: 'HC — CURRENT', toLabel: 'to plan', planLabel: 'plan', projectedToLabel: 'to plan', bottomVariant: 'none', wholeNumber: false },
] as const;

const MaestroCardsRow: React.FC<MaestroCardsRowProps> = ({
    projection,
    shiftKpiComparisons,
    shiftTimeContext,
    nowMs,
}) => {
    const kpiOf = (type: ShiftKpiType): ShiftKpiComparison | undefined =>
        (shiftKpiComparisons ?? []).find((k) => k.kpiType === type);

    return (
        <Row spacing="400" widths="fill" alignmentVertical="stretch" alignmentHorizontal="start">
            {CARD_CONFIGS.map((card) => {
                // Volume is sourced from the ALPS projection; the rest from shiftKpiComparisons.
                const kpi = kpiOf(card.kpiType);
                const actualValue =
                    card.kpiType === ShiftKpiType.VOLUME_COMPLETED
                        ? projection.actualValue
                        : kpi?.actualValue ?? null;
                const targetValue =
                    card.kpiType === ShiftKpiType.VOLUME_COMPLETED
                        ? projection.targetValue
                        : kpi?.targetValue ?? null;
                const comparisonStatus =
                    card.kpiType === ShiftKpiType.VOLUME_COMPLETED
                        ? projection.comparisonStatus
                        : kpi?.comparisonStatus;

                const cardProjection = deriveKpiProjection(
                    card.kpiType,
                    actualValue,
                    targetValue,
                    shiftTimeContext,
                    nowMs
                );

                return (
                    <MaestroCard
                        key={card.key}
                        heading={card.heading}
                        actualValue={actualValue}
                        targetValue={targetValue}
                        comparisonStatus={comparisonStatus}
                        toLabel={card.toLabel}
                        planLabel={card.planLabel}
                        projectedEos={cardProjection.projectedEos}
                        projectedRange={cardProjection.projectedRange}
                        projectedToLabel={card.projectedToLabel}
                        pacePercentage={kpi?.pacePercentage ?? null}
                        capValue={kpi?.capValue ?? null}
                        hoursDeltaToGoal={kpi?.hoursDeltaToGoal ?? null}
                        bottomVariant={card.bottomVariant}
                        wholeNumber={card.wholeNumber}
                    />
                );
            })}
        </Row>
    );
};

export default MaestroCardsRow;
